import { Divider, type DropDownProps } from "antd";
import { useMemo } from "react";
import { Grey15 } from "styles/buildertrendTheme/Colors";

import { cn } from "@btnet/utilities-core";

import { BdsIconTextAa } from "@buildertrend/components";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { toggleEditorMode } from "commonComponents/btWrappers/editor/core/editor.utils";
import { type EditorControlBreakpointNames } from "commonComponents/btWrappers/editor/core/EditorControlBar/EditorControlBar.types";
import {
    CoreControlGroup,
    isCoreControlGroupVisible,
} from "commonComponents/btWrappers/editor/core/EditorControlBar/groups/CoreControlGroup";
import {
    EmbedControlGroup,
    isEmbedControlGroupVisible,
} from "commonComponents/btWrappers/editor/core/EditorControlBar/groups/EmbedControlGroup";
import { getExtensionNames } from "commonComponents/btWrappers/editor/core/extensions/editorExtensionUtils";
import { type IEditor } from "commonComponents/btWrappers/editor/core/useBTEditor.types";

import "../EditorControlBar/common/controlBar.common.less";

interface IEditorTwoStageControlBarProps {
    editor: IEditor;
    editorSize?: EditorControlBreakpointNames;
    dropdownPlacement?: DropDownProps["placement"];
}

export const EditorTwoStageControlBar: React.FC<IEditorTwoStageControlBarProps> = ({
    editor,
    editorSize = "md",
    dropdownPlacement,
}) => {
    const supportedExtensions = useMemo(
        () => getExtensionNames(editor.tiptapEditor.extensionManager.extensions),
        [editor.tiptapEditor.extensionManager.extensions],
    );

    return (
        <div className="EditorControlBar" role="toolbar" aria-orientation="horizontal">
            <div className="flex align-items-center flex-wrap">
                {isEmbedControlGroupVisible(supportedExtensions, editor) && (
                    <EmbedControlGroup
                        editor={editor}
                        supportedExtensions={supportedExtensions}
                        editorSize={editorSize}
                        dropdownPlacement={dropdownPlacement}
                    />
                )}

                <Divider
                    type="vertical"
                    style={{
                        borderLeft: `1px solid ${Grey15}`,
                        height: 32,
                    }}
                />

                <BTButtonDeprecated
                    id="btEditorToggleControls"
                    data-testid="btEditorToggleControls"
                    type="tertiary"
                    icon={<BdsIconTextAa />}
                    className={cn("EditorToggleControls margin-right-xs", {
                        ControlsToggled: editor.editorState.mode === "allControls",
                    })}
                    onClick={() => {
                        toggleEditorMode(
                            editor,
                            editor.editorState.mode === "inlineControlsOnly"
                                ? "allControls"
                                : "inlineControlsOnly",
                        );
                    }}
                />
                {isCoreControlGroupVisible(supportedExtensions, editor) && (
                    <CoreControlGroup
                        editor={editor}
                        supportedExtensions={supportedExtensions}
                        editorSize={editorSize}
                        dropdownPlacement={dropdownPlacement}
                    />
                )}
            </div>
        </div>
    );
};
